<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import datepicker from 'js-datepicker'
import type { UnavailableDays } from '@/types/schedule-appointment'
import AppointmentDateTimePickerContent from '@/lang/date-time-picker.json'
import { useLang } from '@/composables/use-lang'

const props = defineProps({
  unavailableDays: Object,
  minDate: String,
  error: Boolean
})

const datepickerInput = ref(null)
let picker: any = null
const langContent = useLang(AppointmentDateTimePickerContent)

const emit = defineEmits(['handleDateChange', 'handleRemoveSelectedDate'])

const getMinDate = (minDate: string | undefined) => {
  if (!minDate) {
    const min = new Date()
    min.setDate(min.getDate() + 1)
    return min
  }
  const [year, month, day] = minDate.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const getDisabledDates = (unavailableDays: UnavailableDays | undefined) => {
  return unavailableDays?.unavailable?.reduce<Date[]>((result, date) => {
    if (date.day && date.day !== "null" && date.time.length === unavailableDays.times.length) {
      const [year, month, day] = date.day.split('-').map(Number)
      result.push(new Date(year, month - 1, day))
    }

    return result
  }, [])
}

const adjustForTimeZone = (date: Date) => {
  const timeZoneOffset = date.getTimezoneOffset() * 60000
  const localTime = new Date(new Date(date.getTime() - timeZoneOffset).toISOString().slice(0, -1))
  return `${localTime.getFullYear()}-${(localTime.getMonth() + 1).toString().padStart(2, '0')}-${localTime.getDate().toString().padStart(2, '0')}`
}

onMounted(() => {
  picker = datepicker(datepickerInput.value, {
    disabledDates: getDisabledDates(props.unavailableDays as UnavailableDays),
    minDate: getMinDate(props.minDate),
    noWeekends: true,
    formatter: (input: HTMLInputElement, date: Date) => {
      const adjustedDate = adjustForTimeZone(date)
      input.value = adjustedDate
      emit('handleDateChange', adjustedDate)
    },
    onSelect: (_: any, date: Date) => {
      if (!date) {
        emit('handleRemoveSelectedDate')
      }
    }
  })
})

onUnmounted(() => {
  if (picker) {
    picker.remove()
  }
})
</script>

<template>
  <input
    ref="datepickerInput"
    type="text"
    :placeholder="langContent.date_label"
    :class="error ? 'border border-english-vermillion' : ''"
    readonly
    required
  />
  <span v-if="error" class="text-english-vermillion">{{ langContent.date_error }}</span>
</template>
